// Centralized API client that handles JWT authentication
// NEVER use bare fetch() - always use this apiClient
import { getToken, setToken, removeToken } from './tokenStorage';

/**
 * Centralized fetch wrapper that automatically adds JWT token to requests
 * and handles token storage from login responses.
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (headers, body, etc.)
 * @returns Promise<Response>
 */
export const apiFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Get the token from storage
  const token = getToken();

  // Prepare headers
  const headers = new Headers(options.headers);

  // Add Authorization header if token exists
  // Skip for login endpoint to avoid circular issues
  if (token && !url.includes('/auth/login')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Ensure Content-Type is set for non-form-data requests
  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = options.body instanceof FormData;
  if (!headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  // Merge headers with options
  const fetchOptions: RequestInit = {
    ...options,
    headers,
  };

  // Make the request
  const response = await fetch(url, fetchOptions);

  // Handle login response - extract and store token
  if (url.includes('/auth/login') && response.ok) {
    try {
      const data = await response.clone().json(); // Clone response to avoid consuming it
      if (data.token) {
        setToken(data.token);
      }
    } catch (error) {
      // If response is not JSON, ignore token extraction
      console.warn('Failed to extract token from login response', error);
    }
  }

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    removeToken();
    // Redirect to login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  return response;
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (url: string, options?: RequestInit): Promise<Response> => {
  return apiFetch(url, { ...options, method: 'GET' });
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (url: string, body?: any, options?: RequestInit): Promise<Response> => {
  const fetchOptions: RequestInit = {
    ...options,
    method: 'POST',
  };

  if (body) {
    if (body instanceof FormData) {
      fetchOptions.body = body;
    } else {
      fetchOptions.body = JSON.stringify(body);
    }
  }

  return apiFetch(url, fetchOptions);
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (url: string, body?: any, options?: RequestInit): Promise<Response> => {
  const fetchOptions: RequestInit = {
    ...options,
    method: 'PUT',
  };

  if (body) {
    if (body instanceof FormData) {
      fetchOptions.body = body;
    } else {
      fetchOptions.body = JSON.stringify(body);
    }
  }

  return apiFetch(url, fetchOptions);
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (url: string, options?: RequestInit): Promise<Response> => {
  return apiFetch(url, { ...options, method: 'DELETE' });
};
